import type { GuardTag, ParsedMap, Vec2 } from './types.js';

/**
 * ASCII map.
 *  '#' wall, '.' floor, 'L' light source,
 *  'P' player spawn, 'E' extraction,
 *  'g'/'h'/'i' guard patrol nodes.
 */
export const DEFAULT_MAP: readonly string[] = [
  '############################',
  '#P.........#...............#',
  '#..........#......L........#',
  '#.....L....#...........h...#',
  '#..........#######.........#',
  '#..........................#',
  '#######.....g........#######',
  '#..........................#',
  '#......##.........##.......#',
  '#..L...##....i....##....L..#',
  '#......##.........##.......#',
  '#..........................#',
  '#######........g....########',
  '#..........................#',
  '#..........#######.........#',
  '#......L...#.......L.......#',
  '#..........#..............E#',
  '############################',
];

const TILE = 32;
const LIGHT_RADIUS = 110;
const GUARD_TAGS: ReadonlyArray<GuardTag> = ['g', 'h', 'i'];

export function parseMap(mapStr: readonly string[]): ParsedMap {
  const MW = Math.max(...mapStr.map((r) => r.length));
  const MAP = mapStr.map((r) => (r.length < MW ? r + '#'.repeat(MW - r.length) : r));
  const MH = MAP.length;
  const WORLD_W = MW * TILE;
  const WORLD_H = MH * TILE;

  const walls: ParsedMap['walls'] = [];
  const lights: ParsedMap['lights'] = [];
  const guardSpawns: ParsedMap['guardSpawns'] = { g: [], h: [], i: [] };
  const collectibleSpawns: Vec2[] = [];
  const cameraSpawns: ParsedMap['cameraSpawns'] = [];
  let playerStart: Vec2 = { x: 64, y: 64 };
  let exitPos: Vec2 = { x: WORLD_W - 64, y: WORLD_H - 64 };

  // Camera markers — char → initial facing direction (rad).
  // ^ = up (north), v = down (south), < = west, > = east.
  const CAMERA_DIRS: Record<string, number> = {
    '^': -Math.PI / 2,
    v: Math.PI / 2,
    '<': Math.PI,
    '>': 0,
  };

  for (let y = 0; y < MH; y++) {
    const row = MAP[y]!;
    for (let x = 0; x < MW; x++) {
      const c = row[x]!;
      const cx = x * TILE + TILE / 2;
      const cy = y * TILE + TILE / 2;
      if (c === '#') {
        walls.push({ x: x * TILE, y: y * TILE, w: TILE, h: TILE });
      } else if (c === 'L') {
        lights.push({ x: cx, y: cy, r: LIGHT_RADIUS });
      } else if (c === 'P') {
        playerStart = { x: cx, y: cy };
      } else if (c === 'E') {
        exitPos = { x: cx, y: cy };
      } else if (c === '*') {
        collectibleSpawns.push({ x: cx, y: cy });
      } else if (c in CAMERA_DIRS) {
        cameraSpawns.push({ pos: { x: cx, y: cy }, baseDir: CAMERA_DIRS[c]! });
      } else if (GUARD_TAGS.includes(c as GuardTag)) {
        guardSpawns[c as GuardTag].push({ x: cx, y: cy });
      }
    }
  }

  return {
    MAP,
    MW,
    MH,
    TILE,
    WORLD_W,
    WORLD_H,
    walls,
    lights,
    guardSpawns,
    collectibleSpawns,
    cameraSpawns,
    playerStart,
    exitPos,
  };
}

/** A single spawn point gets a fabricated second node so the guard still moves. */
export function makePatrol(arr: Vec2[], fallbackOffset: Vec2 = { x: 80, y: 0 }): Vec2[] | null {
  if (arr.length >= 2) return arr.slice();
  if (arr.length === 1) {
    const first = arr[0]!;
    return [first, { x: first.x + fallbackOffset.x, y: first.y + fallbackOffset.y }];
  }
  return null;
}
